import MaterialTable, { MTableToolbar } from 'material-table';
import { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    OutlinedInput,
    InputAdornment,
    Tooltip,
    Popover,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSelector, useDispatch } from "react-redux";
import Popup from "../../../../Base/views/Popup";
import RaiseRegularizationPopup from "../MyRegularization/RaiseRegularizationPopup";
import { UpsertMyAttendanceRegularizationAction } from '../../../../../../store/Leave/Regularization/Action';
import { GetTimeDataHelper } from '../../../helper/Team/TeamHelper';
import CommonDatePicker from '../../../../Base/views/CommonDatePicker';
import { ConvertDate, ConvertTime, DateConvertInDbFormat, formatTimeDuration, getEmployeeId } from '../../../../Base/helper/baseFunctions';
import { toast } from 'react-toastify';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import ClearIcon from "@mui/icons-material/Clear";
import {formatAttendanceRow} from '../../../helper/TimeHelper/TimeHelper';



const MyAttendanceHistory = ({ faceRecogFlow }) => {
    const dispatch = useDispatch();
    const [empdata, setEmpData] = useState([]);
    const date = new Date();
    const { minDojYear } = useSelector(state => state?.AncillaryReducer?.WorkSpaceDojReducer);
    const currentDate = new Date();

    const default_date = `${date?.getFullYear() + '-' + ("0" + (date?.getMonth() + 1)).slice(-2) + '-' + ("0" + date?.getDate()).slice(-2)}`;
    const [filters, setFilters] = useState({
        employee_id: "",
        full_date: default_date,
        from_date: DateConvertInDbFormat(),
        to_date: DateConvertInDbFormat(),
        dateRangeOption: "",
    });


    const [isLoading, setIsLoading] = useState(false);
    const [isCsvLoading, setIsCsvLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogRowData, setDialogRowData] = useState(null);
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [customFromDate, setCustomFromDate] = useState(filters.from_date);
    const [customToDate, setCustomToDate] = useState(filters.to_date);
    const [anchorEl, setAnchorEl] = useState(null);

    const renderTimeCell = (timeValue) => {
        if (!timeValue) return "-";
        const dateObj = new Date(timeValue);
        const day = String(dateObj.getUTCDate()).padStart(2, "0");
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthName = months[dateObj.getUTCMonth()];
        const formattedTime = dateObj.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
            timeZone: "UTC",
        });
        const displayStr = `${day} ${monthName}, ${formattedTime}`;
        const detailedStr = ConvertTime(timeValue, true);
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <span>{displayStr}</span>
                <Tooltip title={detailedStr} arrow>
                    <InfoOutlinedIcon
                        fontSize="small"
                        color="primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                    />
                </Tooltip>
            </div>
        );
    };

    const renderModeCell = (modeVal, isFaceAttendance, locationDisplayName, locationDistance) => {
        if (!modeVal || modeVal === "-") return "-";
        
        if (isFaceAttendance && locationDisplayName) {
            const tooltipText = (
                <>
                    <div>• Marked at: {locationDisplayName}</div>
                    {locationDistance && (
                        <div>• Distance from office: {locationDistance}</div>
                    )}
                </>
            );
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>{modeVal}</span>
                    <Tooltip title={tooltipText} arrow>
                        <InfoOutlinedIcon
                            fontSize="small"
                            color="primary"
                            style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                    </Tooltip>
                </div>
            );
        }
        
        return modeVal;
    };

    const handleSubmitRegularization = (submittedData) => {
        const dateObj = new Date(submittedData?.record_date);
        const f_year = dateObj.getFullYear().toString();
        const f_month = (dateObj.getMonth() + 1).toString();
        
        const checkIn = getAttendanceDateTime(submittedData?.check_in_time);
        const checkOut = getAttendanceDateTime(submittedData?.check_out_time);
        
        dispatch(
            UpsertMyAttendanceRegularizationAction({
                employee_fk: getEmployeeId(),
                month: f_month,
                year: f_year,
                in_date: checkIn.date,
                out_date: checkOut.date,
                in_time: checkIn.time,
                out_time: checkOut.time,
                attendance_regularisation_pk: null,
                tab: "Self",
                status: "Submitted",
                actionType: "add",
            })
        );
    };

    useEffect(() => {
        setIsLoading(true);
        GetTimeDataHelper({
            from_date: filters?.from_date,
            to_date: filters?.to_date,
            emp_id: `{${getEmployeeId()}}`,
            reporting_manager_emp_id: null,
            ats_work_location_json: null,
            business_unit: null,
            employee_no: null,
            shift_ids: null,
            employment_type_ids: null
        }).then((res) => {
            const formatted = (res?.data?.data || []).map(formatAttendanceRow);
            setEmpData(formatted);
        }).finally(() => {
            setIsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleFilter = (event, value) => {
        const MAX_RANGE = 45;
        if (["employee_id"].includes(event)) {
            setFilters({ ...filters, [event]: value });
        }
        if (["from_date"].includes(event?.target?.name)) {
            let newFromDate = event?.target?.value;
            let newToDate = filters.to_date;

            const diff = getDayDiff(newFromDate, newToDate);

            if (diff > MAX_RANGE) {
                // auto-shift TO DATE
                const candidateToDate = addDays(newFromDate, MAX_RANGE);
                newToDate =
                    new Date(candidateToDate) > new Date(currentDate)
                        ? DateConvertInDbFormat(currentDate)
                        : candidateToDate;
            }

            setFilters({
                ...filters,
                from_date: newFromDate,
                to_date: newToDate,
            });
            return;
        }

        if (["to_date"].includes(event?.target?.name)) {
            let newToDate = event?.target?.value;
            let newFromDate = filters.from_date;

            const diff = getDayDiff(newFromDate, newToDate);

            if (diff > MAX_RANGE) {
                newFromDate = subtractDays(newToDate, MAX_RANGE);
            }
            setFilters({
                ...filters,
                from_date: newFromDate,
                to_date: newToDate,
            });
        }
    };

    const handleDateRangeChange = (option) => {
        const today = new Date();
        let fromDate, toDate;

        if (option === "custom") {
            setShowCustomRange(true);
            setFilters({ ...filters, dateRangeOption: option });
            return;
        } else {
            setShowCustomRange(false);
        }

        switch (option) {
            case "today":
                fromDate = toDate = today;
                break;

            case "yesterday":
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                fromDate = toDate = yesterday;
                break;

            case "last7":
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(today.getDate() - 6);
                fromDate = sevenDaysAgo;
                toDate = today;
                break;

            case "lastMonth":
                fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                toDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;

            default:
                return;
        }

        const formatDate = (date) =>
            `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${(
                "0" + date.getDate()
            ).slice(-2)}`;

        const formattedFrom = formatDate(fromDate);
        const formattedTo = formatDate(toDate);

        setCustomFromDate(formattedFrom);
        setCustomToDate(formattedTo);

        setFilters({
            ...filters,
            from_date: formattedFrom,
            to_date: formattedTo,
            dateRangeOption: option,
        });
    };
    const handleClear = () => {
        const formattedToday = DateConvertInDbFormat();
        setCustomFromDate(formattedToday);
        setCustomToDate(formattedToday);
        setFilters({
            ...filters,
            dateRangeOption: "",
            from_date: formattedToday,
            to_date: formattedToday,
        });
        setShowCustomRange(false);
    };
    const handleCustomDateChange = (name, value) => {
        const formattedValue = value
            ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}`
            : "";

        const MAX_RANGE = 45;
        if (name === "from_date") {
            let newFromDate = formattedValue;
            let newToDate = customToDate;

            const diff = getDayDiff(newFromDate, newToDate);

            if (diff > MAX_RANGE) {
                const candidateToDate = addDays(newFromDate, MAX_RANGE);
                newToDate =
                    new Date(candidateToDate) > new Date(currentDate)
                        ? DateConvertInDbFormat(currentDate)
                        : candidateToDate;
            }
            setCustomFromDate(newFromDate);
            setCustomToDate(newToDate);
        } else if (name === "to_date") {
            let newToDate = formattedValue;
            let newFromDate = customFromDate;

            const diff = getDayDiff(newFromDate, newToDate);

            if (diff > MAX_RANGE) {
                newFromDate = subtractDays(newToDate, MAX_RANGE);
            }
            setCustomFromDate(newFromDate);
            setCustomToDate(newToDate);
        }
    };
    const handleApplyCustomRange = () => {
        setFilters({
            ...filters,
            from_date: customFromDate,
            to_date: customToDate,
        });
    };
    const handleExport = () => {
        setIsCsvLoading(true);

        try {
            const headers = [
                { label: "Name", key: "employee_name" },
                { label: "Attendance Date", key: "record_date" },
                { label: "Work Location", key: "work_location_name" },
                { label: "In Mode", key: "in_mode" },
                { label: "Out Mode", key: "out_mode" },
                { label: "In Location", key: "in_location_display_name" },
                { label: "In Distance from office", key: "in_location_distance" },
                { label: "Out Location", key: "out_location_display_name" },
                { label: "Out Distance from office", key: "out_location_distance" },
                { label: "First Check-In", key: "in_time" },
                { label: "Last Check-Out", key: "out_time" },
                { label: "Total Hours", key: "total_seconds" },
            ];

            const csvData = [headers.map(h => h.label)];

            empdata?.forEach(row => {
                const rowData = headers.map(h => {
                    let value = row[h.key];

                    // Apply ConvertTime only for time fields
                    if (h.key === "in_time" && value) {
                        value = ConvertTime(value, true);
                    }
                    if (h.key === "out_time" && value) {
                        value = ConvertTime(value, true);
                    }
                    if (h.key === "total_seconds" && value) {
                        value = formatTimeDuration(value);
                    }
                    if (h.key === "total_seconds" && value) {
                        value = formatTimeDuration(value);
                    }
                    if (h.key === "record_date" && value) {
                        value = ConvertDate(value);
                    }

                    // CSV-safe formatting
                    if (value === null || value === undefined) return "";
                    if (typeof value === "string") {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                });

                csvData.push(rowData);
            });

            const csv = csvData.map(row => row.join(",")).join("\n");

            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = "TeamTimeData.csv";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(`Error generating CSV: ${err.message}`);
        } finally {
            setIsCsvLoading(false);
        }
    };


    return (
        <>
            <Grid container spacing={2} sx={{ pt: { xs: 1, sm: 0 }, pb: 1 }} alignItems="center" justifyContent="flex-end">
                <Grid item xs={6} sm={3.5} md={2.5}>
                    <FormControl fullWidth size="small" variant="outlined">
                        <OutlinedInput
                            readOnly
                            value={
                                filters.from_date === filters.to_date
                                    ? ConvertDate(filters.from_date)
                                    : `${ConvertDate(filters.from_date)} - ${ConvertDate(filters.to_date)}`
                            }
                            onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setShowCustomRange(filters.dateRangeOption === "custom");
                            }}
                            placeholder="Date"
                            startAdornment={
                                <InputAdornment position="start" sx={{ marginRight: 1 }}>
                                    <InsertInvitationIcon color="action" sx={{ fontSize: 22, mt: 0.2 }} />
                                </InputAdornment>
                            }
                            endAdornment={
                                filters?.dateRangeOption ? (
                                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClear();
                                            }}
                                            sx={{ p: 0.3 }}
                                        >
                                            <ClearIcon fontSize="small" sx={{ fontSize: 22, mt: 0.2 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : (
                                    <InputAdornment position="end">
                                        <ArrowDropDownIcon sx={{ color: "action.active", pointerEvents: "none" }} />
                                    </InputAdornment>
                                )
                            }
                            sx={{
                                height: 37,
                                backgroundColor: "#fff",
                                cursor: "pointer",
                                "& .MuiOutlinedInput-input": {
                                    cursor: "pointer",
                                    paddingLeft: "4px !important",
                                    paddingRight: "0px !important",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "12px",
                                },
                            }}
                        />
                        <Popover
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={(e, reason) => {
                                if (reason === "backdropClick") {
                                    const isPickerElement = e?.target && (
                                        e.target.closest?.(".MuiPickersPopper-root") ||
                                        e.target.closest?.(".MuiDialog-root") ||
                                        e.target.closest?.(".MuiTooltip-popper")
                                    );
                                    if (isPickerElement) return;
                                }
                                setAnchorEl(null);
                            }}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            PaperProps={{
                                sx: {
                                    width: 340,
                                    mt: 0.5,
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                                    borderRadius: "8px",
                                }
                            }}
                        >
                            {!showCustomRange ? (
                                <Box>
                                    <MenuItem disabled sx={{ fontWeight: "bold", py: 1, px: 2 }}>
                                        Date
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleDateRangeChange("today"); setAnchorEl(null); }} sx={{ py: 1, px: 2 }}>
                                        Today
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleDateRangeChange("yesterday"); setAnchorEl(null); }} sx={{ py: 1, px: 2 }}>
                                        Yesterday
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleDateRangeChange("last7"); setAnchorEl(null); }} sx={{ py: 1, px: 2 }}>
                                        Previous 7 days
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleDateRangeChange("lastMonth"); setAnchorEl(null); }} sx={{ py: 1, px: 2 }}>
                                        Last Month
                                    </MenuItem>
                                    <MenuItem onClick={() => setShowCustomRange(true)} sx={{ py: 1, px: 2 }}>
                                        Custom Range
                                    </MenuItem>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setShowCustomRange(false)}
                                            sx={{ mr: 1, p: 0.5 }}
                                        >
                                            <ArrowBackIosNewIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                            Custom Range
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                            <CommonDatePicker
                                                name="from_date"
                                                label={<span>
                                                    From Date&nbsp;
                                                    <Tooltip title="You can select up to 45 days at a time. Dates will adjust automatically if the range is longer." placement="top-start" arrow>
                                                        <InfoOutlinedIcon fontSize="small" color="action" style={{ verticalAlign: "middle", cursor: "pointer", marginBottom: "4px" }} />
                                                    </Tooltip>
                                                </span>}
                                                size="small"
                                                pickerType="date"
                                                onKeyPress={(e) => e.preventDefault()}
                                                pickerFormat="dd/MM/yyyy"
                                                DefaultDTvalue={customFromDate}
                                                minDate={new Date(new Date(minDojYear).setFullYear(new Date(minDojYear).getFullYear() - 1))}
                                                maxDate={currentDate}
                                                handleChange={(value) => handleCustomDateChange("from_date", value)}
                                            />
                                        </FormControl>
                                        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                            <CommonDatePicker
                                                name="to_date"
                                                label={<span>
                                                    To Date&nbsp;
                                                    <Tooltip title="You can select up to 45 days at a time. Dates will adjust automatically if the range is longer." placement="top-end" arrow>
                                                        <InfoOutlinedIcon fontSize="small" color="action" style={{ verticalAlign: "middle", cursor: "pointer", marginBottom: "4px" }} />
                                                    </Tooltip>
                                                </span>}
                                                pickerType="date"
                                                size="small"
                                                onKeyPress={(e) => e.preventDefault()}
                                                pickerFormat="dd/MM/yyyy"
                                                DefaultDTvalue={customToDate}
                                                minDate={customFromDate}
                                                maxDate={currentDate}
                                                handleChange={(value) => handleCustomDateChange("to_date", value)}
                                            />
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Button
                                            sx={{
                                                fontWeight: 700,
                                                borderRadius: "8px",
                                                fontSize: "13px",
                                                height: "32px",
                                                padding: "4px 16px",
                                                boxShadow: "none",
                                                textTransform: "none",
                                            }}
                                            size="small"
                                            variant="contained"
                                            className="mpl-primary-btn"
                                            onClick={() => {
                                                handleApplyCustomRange();
                                                setAnchorEl(null);
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Popover>
                    </FormControl>
                </Grid>
            </Grid>

            <MaterialTable
                columns={[
                    { title: "Attendance Date", cellStyle: { padding: "10px", textAlign: "left" }, headerStyle: { padding: "10px", textAlign: "left" }, field: "record_date", editable: "never", render: rowData => { return rowData?.record_date ? ConvertDate(rowData?.record_date) : "    " } },
                    {
                        title: "In Mode",
                        field: "in_mode",
                        editable: "never",
                        cellStyle: { padding: "10px", textAlign: "left" },
                        headerStyle: { padding: "10px", textAlign: "left" },
                        render: rowData => renderModeCell(rowData?.in_mode, rowData?.is_face_attendance, rowData?.in_location_display_name, rowData?.in_location_distance),
                    },
                    {
                        title: "Out Mode",
                        field: "out_mode",
                        editable: "never",
                        cellStyle: { padding: "10px", textAlign: "left" },
                        headerStyle: { padding: "10px", textAlign: "left" },
                        render: rowData => renderModeCell(rowData?.out_mode, rowData?.is_face_attendance, rowData?.out_location_display_name, rowData?.out_location_distance),
                    },
                    /* {
                        title: "In Location",
                        editable: "never",
                        cellStyle: { padding: "10px", textAlign: "left" },
                        headerStyle: { padding: "10px", textAlign: "left" },
                        render: (rowData) => {
                            if (!rowData?.is_face_attendance || !rowData?.in_location_display_name) {
                                return "-";
                            }

                            const tooltipText = (
                                <>
                                    <div>• Marked at: {rowData.in_location_display_name}</div>
                                    {rowData.in_location_distance && (
                                        <div>• Distance from office: {rowData.in_location_distance}</div>
                                    )}
                                </>
                            );

                            return (
                                <div style={{ maxWidth: "150px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <span
                                            style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                flex: 1,
                                            }}
                                        >
                                            {rowData.in_location_display_name}
                                        </span>

                                        <Tooltip title={tooltipText} arrow>
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                color="primary"
                                                style={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {rowData.in_location_distance && (
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                            ({rowData.in_location_distance})
                                        </div>
                                    )}
                                </div>
                            );
                        },
                    }, */
                    /* {
                        title: "Out Location",
                        editable: "never",
                        cellStyle: { padding: "10px", textAlign: "left" },
                        headerStyle: { padding: "10px", textAlign: "left" },
                        render: (rowData) => {
                            if (!rowData?.is_face_attendance || !rowData?.out_location_display_name) {
                                return "-";
                            }

                            const tooltipText = (
                                <>
                                    <div>• Marked at: {rowData.out_location_display_name}</div>
                                    {rowData.out_location_distance && (
                                        <div>• Distance from office: {rowData.out_location_distance}</div>
                                    )}
                                </>
                            );

                            return (
                                <div style={{ maxWidth: "150px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <span
                                            style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                flex: 1,
                                            }}
                                        >
                                            {rowData.out_location_display_name}
                                        </span>

                                        <Tooltip title={tooltipText} arrow>
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                color="primary"
                                                style={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {rowData.out_location_distance && (
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                            ({rowData.out_location_distance})
                                        </div>
                                    )}
                                </div>
                            );
                        },
                    }, */

                    { title: "First Check-In", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" }, field: "in_time", editable: "never", render: rowData => renderTimeCell(rowData?.in_time) },

                    { title: "Last Check-Out", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" }, field: "out_time", editable: "never", render: rowData => renderTimeCell(rowData?.out_time) },

                    { title: "Total Hours", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" }, field: "total_seconds", editable: "never", render: rowData => rowData?.total_seconds ? formatTimeDuration(rowData?.total_seconds) : "" },
                    {
                        title: "Actions",
                        field: "action",
                        width: "8%",
                        grouping: false,
                        sorting: false,
                        headerStyle: { textAlign: "center" },
                        cellStyle: { padding: "10px", textAlign: "center" },
                        render: (rowData) => {
                            const isBulkOrTimesheet = 
                                rowData?.in_mode === "Time-Bulk Attendance" || 
                                rowData?.out_mode === "Time-Bulk Attendance" ||
                                rowData?.in_mode === "Timesheet Attendance" || 
                                rowData?.out_mode === "Timesheet Attendance";

                            if (isBulkOrTimesheet) return null;

                            return (
                                <Tooltip title="Raise Regularization">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDialogRowData({
                                                record_date: rowData?.record_date,
                                                in_time: rowData?.in_time,
                                                out_time: rowData?.out_time,
                                            });
                                            setDialogOpen(true);
                                        }}
                                        sx={{
                                            padding: "0px", width: "30px", height: "30px", cursor: "pointer"
                                        }}
                                    >
                                        <EditCalendarIcon fontSize="small" sx={{ color: "#637381" }} />
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                    }

                ]}
                data={empdata}
                isLoading={isLoading}
                options={{
                    maxBodyHeight: "calc(100vh - 280px)",
                    minBodyHeight: "calc(100vh - 280px)",
                    rowStyle: { fontSize: "13px" },
                    headerStyle: { padding: "10px 10px 10px 13px", fontWeight: "bold" },
                    sorting: true,
                    search: true,
                    searchFieldAlignment: "right",
                    searchAutoFocus: true,
                    searchFieldVariant: "standard",
                    paging: true,
                    pageSizeOptions: [25, 50],
                    pageSize: 25,
                    paginationType: "stepped",
                    showFirstLastPageButtons: false,
                    paginationPosition: "bottom",
                    exportFileName: "Bio Metric",
                    exportAllData: true,
                    exportButton: false,
                    columnsButton: true,
                }}
                title=""
                actions={[
                    {
                        icon: () => isCsvLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon />,
                        tooltip: "Export CSV",
                        isFreeAction: true,
                        onClick: handleExport,
                        hidden: empdata?.length === 0
                    },
                ]}
                components={{
                    Toolbar: (props) => (
                        <Box sx={{ minHeight: "5px" }}>
                            <MTableToolbar {...props} />
                        </Box>
                    ),
                }}
            />

            {dialogOpen && (
                <Popup
                    title="Add Attendance Regularization"
                    openPopup={dialogOpen}
                    setOpenPopup={setDialogOpen}
                >
                    <RaiseRegularizationPopup
                        editedRecord={dialogRowData}
                        setOpenPopup={setDialogOpen}
                        onSubmit={(data) => {
                            handleSubmitRegularization(data);
                        }}
                        addMode={true}
                        startOfMonth={dialogRowData ? new Date(new Date(dialogRowData.record_date).getFullYear(), new Date(dialogRowData.record_date).getMonth(), 1) : new Date()}
                        endOfMonth={dialogRowData ? new Date(new Date(dialogRowData.record_date).getFullYear(), new Date(dialogRowData.record_date).getMonth() + 1, 0) : new Date()}
                    />
                </Popup>
            )}

        </>
    );
};

const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return DateConvertInDbFormat(d);
};

const subtractDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - days);
    return DateConvertInDbFormat(d);
};

const getDayDiff = (from, to) => {
    const start = new Date(from);
    const end = new Date(to);
    return Math.abs(
        Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
    );
};


const getLocalISOString = (date) => {
  if (!date) return null;

  const offsetInMinutes = date?.getTimezoneOffset();
  const localTime = new Date(date.getTime() - offsetInMinutes * 60000);

  return localTime.toISOString().slice(0, -1);
};

const getAttendanceDateTime = (value) => {
  if (!value) {
    return {
      date: null,
      time: null,
    };
  }
  if (typeof value === "string") {
    return {
      date: value.split("T")[0],
      time: value,
    };
  }
  if (value instanceof Date) {
    return {
      date: DateConvertInDbFormat(value),
      time: getLocalISOString(value),
    };
  }
  return {
    date: null,
    time: null,
  };
};

export default MyAttendanceHistory;